import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Hourglass, Lock } from 'lucide-react'
import type { ChecklistItem } from '@/types/api'
import {
  getBlockedOnboardingFallbackHref,
  getFirstIncompleteChecklistKey,
  getNextOnboardingHref,
  getOnboardingCurrentStepIndex,
  mergeChecklistItems,
  type CddsNavContext,
} from '@/lib/onboarding-next-step'
import { NextStepCard } from '@/components/portal/NextStepCard'

interface BlockedOnboardingStateProps {
  checklist: ChecklistItem[]
  clientId: string | null
  pendingApproval?: boolean
  cddsNav?: CddsNavContext
}

export function BlockedOnboardingState({
  checklist,
  clientId,
  pendingApproval = false,
  cddsNav,
}: BlockedOnboardingStateProps) {
  const { t } = useTranslation()
  const firstKey = getFirstIncompleteChecklistKey(checklist)
  const nextHref = getNextOnboardingHref(checklist, clientId, cddsNav)
  const ctaHref = nextHref ?? getBlockedOnboardingFallbackHref(checklist, clientId, cddsNav)
  const waitingApproval = pendingApproval || firstKey === 'perjanjian_accepted'

  const steps = mergeChecklistItems(checklist)
  const currentIndex = getOnboardingCurrentStepIndex(steps, { awaitingApproval: waitingApproval, cddsNav })
  const currentKey = currentIndex >= 0 ? steps[currentIndex]?.key : undefined
  const nextStepTitle =
    currentKey && currentKey !== 'perjanjian_accepted'
      ? t(`home.nextStep.${currentKey}.title`, { defaultValue: t(`checklist.${currentKey}`) })
      : null

  return (
    <NextStepCard
      titleId="funding-blocked-title"
      as="div"
      className="prod-blocked-state"
      tone={waitingApproval ? 'waiting' : 'action'}
      icon={
        waitingApproval ? (
          <Hourglass className="size-5" aria-hidden="true" />
        ) : (
          <Lock className="size-5" aria-hidden="true" />
        )
      }
      currentIndex={currentIndex}
      completed={steps.map((item) => item.completed)}
      title={t('fundingPage.blockedTitle')}
      body={
        <>
          <p>{waitingApproval ? t('fundingPage.waitingApprovalHelper') : t('fundingPage.blockedHelper')}</p>
          {!waitingApproval && nextStepTitle ? (
            <p className="prod-next-step__next">{t('fundingPage.nextStepLine', { step: nextStepTitle })}</p>
          ) : null}
        </>
      }
      action={
        <Link
          to={waitingApproval ? '/dashboard' : ctaHref}
          className={waitingApproval ? 'prod-btn prod-btn--outline' : 'prod-btn prod-btn--gold prod-next-step__cta'}
        >
          {waitingApproval ? t('fundingPage.backToDashboard') : t('fundingPage.continueOnboarding')}
        </Link>
      }
    />
  )
}
